import CheckoutPayment from './CheckoutPayment';
import paymentTypes from './paymentTypes';
import { nullSafeToString } from '../lib/util';

/*
 * Rebuilds Checkout#payment from what the server already holds, so a checkout resumed after a refresh
 * does not send the shopper back to re-pick a payment method they already chose.
 *
 * Kept out of CheckoutAPI and free of any network call so the rules below are unit testable: getting
 * one of them wrong produces a checkout that looks correct and fails at submit, which is the worst
 * possible failure mode for this particular screen.
 */

const PAYMENT_TYPE_VALUES = Object.keys(paymentTypes).map(key => paymentTypes[key]);

/*
 * GET /projects/v2/{id}/creditCard never returns a raw one-time nonce. ProjectCreditCardMapper
 * #getPaymentMethodToken substitutes a literal placeholder whenever the project holds one - "new" for
 * a card, "PayPal" for PayPal - and returns the real vault token otherwise.
 *
 * That makes these two strings the only signal distinguishing "saved method, reusable" from "a nonce
 * that has already been spent". Treating a placeholder as a token would build a CheckoutPayment that
 * passes validation, renders as a saved card, and then fails at authorization.
 *
 * Matched case insensitively on purpose: the cost of over-matching is a shopper re-picking a payment
 * method, while the cost of under-matching is a broken charge.
 */

const ONE_TIME_TOKEN_PLACEHOLDERS = Object.freeze(['new', 'paypal']);

function isOneTimeTokenPlaceholder (token) {
    return ONE_TIME_TOKEN_PLACEHOLDERS.includes(nullSafeToString(token).trim().toLowerCase());
}

/*
 * The API stores a display-only masked number and hands it back re-masked ("XXXX-XXXX-XXXX-XX42"), so
 * the last two digits are read off the end of that string rather than from a field of their own.
 */

function lastTwoFrom (maskedNumber) {
    const lastTwo = nullSafeToString(maskedNumber).slice(-2);

    return /^\d{2}$/u.test(lastTwo) ? lastTwo : '';
}

/**
 * Rebuild the payment a shopper already chose, from a project and its stored card.
 *
 * Restores a terms/PO payment from the project alone, and a saved card or vaulted PayPal account from
 * its vault token. A newly entered card cannot be restored - its Braintree nonce is single use and the
 * API does not hand it back - so this reports null and the shopper re-enters the card, which is the
 * only correct outcome.
 *
 * @param {Object} projectData - The raw `GET /projects/v2/{id}` payload.
 * @param {?Object} creditCardData - The raw `GET /projects/v2/{id}/creditCard` payload, or null when the project has no card.
 * @returns {?CheckoutPayment} The restored payment, or null when there is nothing safe to restore.
 */

export default function restorePayment (projectData, creditCardData) {
    const project = projectData || {};
    const type = nullSafeToString(project.checkoutPaymentType);

    // Nothing chosen yet, or a type this SDK does not drive - either way there is nothing to restore.
    if (!PAYMENT_TYPE_VALUES.includes(type)) {
        return null;
    }

    const po = nullSafeToString(project.po);

    try {
        if (type === paymentTypes.TERMS) {
            return new CheckoutPayment({ type, po });
        }

        const card = creditCardData || {};
        const token = card.paymentMethodToken;

        // No card stored, or the project holds a spent one-time nonce.
        if (!nullSafeToString(token).trim() || isOneTimeTokenPlaceholder(token)) {
            return null;
        }

        return new CheckoutPayment({
            type,
            po,
            savedPaymentMethodToken: token,
            cardHolder: card.cardHolder,
            /*
             * The API reports its own order_cc_types name here, not Braintree's string. They agree
             * after cardTypes.js normalizes for lookup ("American Express" -> americanexpress), so it
             * round trips on the next save.
             */
            braintreeCardType: card.type,
            lastTwo: lastTwoFrom(card.number),
        });
    } catch (e) {
        /*
         * A payment that cannot be rebuilt must never stop a checkout loading - the shopper can always
         * pick a method again, but they cannot recover from start() rejecting.
         */
        console.warn('Collaterate.Checkout: the saved payment method could not be restored. The shopper will need to choose one again.');

        return null;
    }
}
